import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  IconButtonOwnProps,
  Typography,
} from '@mui/material';
import {
  Fragment,
  ReactNode,
  cloneElement,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { BUTTON_COLORS, themeColors as COLORS } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { CloseButton } from './components/SharedDialogHookComponents';
import { wrapDatadogReplayTitle, Button } from '@act/storybook';

interface AlertContentProps {
  $hideActions: boolean;
}
const AlertContent = styled(DialogContent)<AlertContentProps>`
  display: grid;
  place-items: center;
  row-gap: 8px;
  padding: ${({ $hideActions }) =>
    $hideActions ? '12px 24px 28px !important' : '12px 24px 20px !important'};
`;

interface AlertIconProps {
  $borderColor: string;
}
const AlertIcon = styled(IconButton)<AlertIconProps>`
  padding: 8px;
  pointer-events: none;
  background-color: ${({ $borderColor }) => COLORS[$borderColor]};
`;

const AlertDialogTitle = styled(DialogTitle)`
  width: 400px;
  box-sizing: border-box;
`;

const AlertActions = styled(DialogActions)`
  padding: 8px 24px;
`;

export interface ConfirmationAlertUniqueProps {
  title: ReactNode;
  icon?: React.ReactElement;
  iconColor?: IconButtonOwnProps['color'];
  iconBorderColor?: keyof typeof COLORS;
  avatarCaption?: string;
  message: ReactNode;
  cancelText?: string;
  confirmationText?: string;
  confirmButtonColor?: BUTTON_COLORS;
  showClose?: boolean;
  hideIcon?: boolean;
  hideActions?: boolean;
}

export type ConfirmationAlertDialogProps = GenericDialogProps<boolean> &
  ConfirmationAlertUniqueProps;

export type ConfirmationAlertCallbackInput = boolean;

export const ConfirmationAlertDialog = ({
  title,
  icon = <CheckCircleOutlineIcon />,
  iconColor = 'success',
  iconBorderColor = 'successContainer',
  avatarCaption,
  message,
  cancelText = 'Cancel',
  confirmationText = 'Confirm',
  confirmButtonColor = 'primary',
  showClose = false,
  hideIcon = false,
  hideActions = false,
  open,
  setOpen,
  callback,
}: ConfirmationAlertDialogProps) => {
  const handleClose = useCallback(
    (confirmed: boolean) => {
      setOpen(false);
      callback(confirmed);
    },
    [setOpen, callback]
  );
  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="xs">
      <AlertDialogTitle>
        <Typography variant="titleLarge">
          {wrapDatadogReplayTitle(title)}
        </Typography>
        {showClose && (
          <CloseButton aria-label="close" onClick={() => handleClose(false)}>
            <CloseIcon />
          </CloseButton>
        )}
      </AlertDialogTitle>
      <AlertContent $hideActions={hideActions}>
        <Fragment>
          {!hideIcon && (
            <Fragment>
              <AlertIcon color={iconColor} $borderColor={iconBorderColor}>
                {cloneElement(icon, { sx: { fontSize: '96px' } })}
              </AlertIcon>
              <Typography variant="titleMedium">{avatarCaption}</Typography>
              <Typography variant="bodyMedium">{message}</Typography>
            </Fragment>
          )}
          {hideIcon && <Typography variant="bodyLarge">{message}</Typography>}
        </Fragment>
      </AlertContent>
      <Divider />
      {!hideActions && (
        <AlertActions>
          <Button trackingName="close-button" onClick={() => handleClose(false)} variant="text">
            {cancelText}
          </Button>
          <Button trackingName="close-button"
            onClick={() => handleClose(true)}
            variant="contained"
            color={confirmButtonColor}
            debounced
          >
            {confirmationText}
          </Button>
        </AlertActions>
      )}
    </Dialog>
  );
};
